import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Calendar, MapPin, DollarSign, MessageSquare, User, LogOut, Mail, Shield, Plane, Eye, Share2, Copy, Loader2, Download, FileText, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MembersDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [paidTrips, setPaidTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchMessages();
    fetchPaidTrips();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/members/dashboard`, { withCredentials: true });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/members/messages`, { withCredentials: true });
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPaidTrips = async () => {
    setLoadingTrips(true);
    try {
      const response = await axios.get(`${API_URL}/itinerary/member/trips`, { withCredentials: true });
      setPaidTrips(response.data.trips || []);
    } catch (error) {
      console.error('Error fetching paid trips:', error);
    } finally {
      setLoadingTrips(false);
    }
  };

  const copyMagicLink = (link) => {
    navigator.clipboard.writeText(link);
    sonnerToast.success('Link copied to clipboard!');
  };

  const generateMagicLink = async (bookingId) => {
    try {
      const response = await axios.post(
        `${API_URL}/itinerary/magic-link/${bookingId}`,
        {},
        { withCredentials: true }
      );
      sonnerToast.success('Magic link generated!');
      // Refresh trips to get the new magic link
      fetchPaidTrips();
      return response.data.magic_link;
    } catch (error) {
      sonnerToast.error('Failed to generate link');
      return null;
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/members/messages`, 
        { content: newMessage },
        { withCredentials: true }
      );
      toast({ title: "Message Sent", description: "We'll respond shortly!" });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast({ title: "Failed to send", description: "Please try again", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#84bc00] mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-32 pt-48">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#84bc00] to-[#6a9600] rounded-3xl p-8 mb-8 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-4 border-white" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <User size={32} className="text-[#84bc00]" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-black text-gray-900">Welcome back, {user?.name}!</h1>
                  {user?.role === 'admin' && (
                    <Badge className="bg-gray-900 text-[#84bc00] font-bold">ADMIN</Badge>
                  )}
                </div>
                <p className="text-gray-800 font-semibold text-lg">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Admin Panel Link - Only show for admin users */}
              {user?.role === 'admin' && (
                <Button 
                  onClick={() => navigate('/admin')} 
                  className="bg-gray-900 hover:bg-gray-800 text-[#84bc00] font-bold"
                  data-testid="admin-panel-btn"
                >
                  <Shield size={18} className="mr-2" />
                  ADMIN PANEL
                </Button>
              )}
              <Button onClick={handleLogout} variant="outline" className="border-2 border-gray-900 hover:bg-gray-900 hover:text-[#84bc00] font-bold text-gray-900">
                <LogOut size={18} className="mr-2" />
                LOGOUT
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plane className="text-[#84bc00]" size={24} />
                <span>My Trips</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-black">{paidTrips.length}</p>
              <p className="text-sm text-gray-600">Booked trips</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="text-[#84bc00]" size={24} />
                <span>Quotes</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-black">{dashboardData?.quotes?.length || 0}</p>
              <p className="text-sm text-gray-600">Pending quotes</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="text-[#84bc00]" size={24} />
                <span>Itineraries</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-black">{dashboardData?.itineraries?.length || 0}</p>
              <p className="text-sm text-gray-600">Confirmed trips</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageSquare className="text-[#84bc00]" size={24} />
                <span>Messages</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-black">{dashboardData?.unread_messages || 0}</p>
              <p className="text-sm text-gray-600">Unread messages</p>
            </CardContent>
          </Card>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="trips" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="trips" className="font-bold">MY TRIPS</TabsTrigger>
            <TabsTrigger value="quotes" className="font-bold">QUOTES</TabsTrigger>
            <TabsTrigger value="itineraries" className="font-bold">ITINERARIES</TabsTrigger>
            <TabsTrigger value="messages" className="font-bold">MESSAGES</TabsTrigger>
          </TabsList>

          {/* My Trips Tab - Shows paid bookings with itinerary access */}
          <TabsContent value="trips">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="text-[#84bc00]" />
                  Your Booked Trips
                </CardTitle>
                <CardDescription>
                  View your trip itineraries and share with travel companions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTrips ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 text-[#84bc00] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading your trips...</p>
                  </div>
                ) : paidTrips.length > 0 ? (
                  <div className="space-y-4">
                    {paidTrips.map((trip) => (
                      <div 
                        key={trip.booking_id} 
                        className="border-2 rounded-xl overflow-hidden hover:border-[#84bc00] transition-all"
                      >
                        <div className="flex flex-col md:flex-row">
                          {/* Trip Image */}
                          {trip.image && (
                            <div 
                              className="w-full md:w-48 h-32 md:h-auto bg-cover bg-center"
                              style={{ backgroundImage: `url(${trip.image})` }}
                            />
                          )}
                          
                          {/* Trip Details */}
                          <div className="flex-1 p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="text-xl font-black">{trip.trip_name}</h3>
                                <div className="flex items-center gap-2 text-gray-600 mt-1">
                                  <MapPin size={14} />
                                  <span className="text-sm">{trip.destination}</span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-sm">{trip.duration}</span>
                                </div>
                              </div>
                              <Badge className={
                                trip.status === 'paid_in_full' ? 'bg-[#84bc00]' :
                                trip.status === 'deposit_paid' ? 'bg-orange-500' : 'bg-gray-500'
                              }>
                                {trip.status === 'paid_in_full' ? 'CONFIRMED' : 
                                 trip.status === 'deposit_paid' ? 'DEPOSIT PAID' : 
                                 trip.status.toUpperCase()}
                              </Badge>
                            </div>
                            
                            {/* Payment Info */}
                            {trip.balance_due > 0 && (
                              <div className="bg-orange-50 rounded-lg p-3 mb-4">
                                <p className="text-sm text-orange-800">
                                  <span className="font-bold">Balance Due:</span> ${trip.balance_due.toLocaleString()}
                                </p>
                              </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex flex-wrap items-center gap-3">
                              <Button
                                onClick={() => navigate(`/itinerary/${trip.booking_id}`)}
                                className="bg-[#84bc00] hover:bg-[#6a9600]"
                                data-testid={`view-itinerary-${trip.booking_id}`}
                              >
                                <Eye size={16} className="mr-2" />
                                View Itinerary
                              </Button>
                              
                              {trip.magic_link ? (
                                <Button
                                  variant="outline"
                                  onClick={() => copyMagicLink(trip.magic_link)}
                                  className="border-[#84bc00] text-[#84bc00]"
                                >
                                  <Copy size={16} className="mr-2" />
                                  Copy Share Link
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  onClick={() => generateMagicLink(trip.booking_id)}
                                  className="border-gray-300"
                                >
                                  <Share2 size={16} className="mr-2" />
                                  Generate Share Link
                                </Button>
                              )}

                              {/* PDF Downloads Dropdown */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" className="border-gray-300">
                                    <Download size={16} className="mr-2" />
                                    Download
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                  <DropdownMenuLabel>English (EN)</DropdownMenuLabel>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${trip.booking_id}?lang=en`, '_blank')}
                                  >
                                    <Receipt size={16} className="mr-2" />
                                    Invoice (EN)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${trip.booking_id}?lang=en`, '_blank')}
                                  >
                                    <FileText size={16} className="mr-2" />
                                    Booking Confirmation (EN)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${trip.booking_id}?lang=en`, '_blank')}
                                  >
                                    <Calendar size={16} className="mr-2" />
                                    Full Itinerary (EN)
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuSeparator />
                                  
                                  <DropdownMenuLabel>Español (ES)</DropdownMenuLabel>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${trip.booking_id}?lang=es`, '_blank')}
                                  >
                                    <Receipt size={16} className="mr-2" />
                                    Factura (ES)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${trip.booking_id}?lang=es`, '_blank')}
                                  >
                                    <FileText size={16} className="mr-2" />
                                    Confirmación (ES)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${trip.booking_id}?lang=es`, '_blank')}
                                  >
                                    <Calendar size={16} className="mr-2" />
                                    Itinerario (ES)
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Plane size={48} className="text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No booked trips yet.</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Once you complete a booking, your trips will appear here with full itinerary access.
                    </p>
                    <Button 
                      className="mt-4 bg-[#84bc00] hover:bg-[#6a9600]" 
                      onClick={() => navigate('/trips')}
                    >
                      BROWSE TRIPS
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Your Travel Quotes</CardTitle>
                <CardDescription>Quotes from your consultation requests</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.quotes?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboardData.quotes.map((quote) => (
                      <div key={quote.quote_id} className="border-2 rounded-lg p-6 hover:border-[#84bc00] transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black">{quote.destination}</h3>
                            <p className="text-sm text-gray-600">{quote.travelers} travelers</p>
                          </div>
                          <Badge className={
                            quote.status === 'approved' ? 'bg-[#84bc00]' :
                            quote.status === 'pending' ? 'bg-[#ffb84d]' : 'bg-gray-500'
                          }>
                            {quote.status.toUpperCase()}
                          </Badge>
                        </div>
                        {quote.estimated_price && (
                          <p className="text-2xl font-bold text-[#84bc00]">${quote.estimated_price}</p>
                        )}
                        {quote.notes && <p className="text-gray-700 mt-2">{quote.notes}</p>}
                        <p className="text-xs text-gray-500 mt-4">
                          Submitted: {new Date(quote.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign size={48} className="text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No quotes yet. Book a consultation to get started!</p>
                    <Button className="mt-4 bg-[#84bc00] hover:bg-[#6a9600]" onClick={() => navigate('/consultation')}>
                      REQUEST CONSULTATION
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Itineraries Tab */}
          <TabsContent value="itineraries">
            <Card>
              <CardHeader>
                <CardTitle>Your Travel Itineraries</CardTitle>
                <CardDescription>Confirmed trips and travel documents</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.itineraries?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboardData.itineraries.map((itin) => (
                      <div key={itin.itinerary_id} className="border-2 rounded-lg p-6 hover:border-[#84bc00] transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-black">{itin.package_name}</h3>
                            <div className="flex items-center space-x-2 text-gray-600 mt-2">
                              <MapPin size={16} />
                              <span>{itin.destination}</span>
                            </div>
                          </div>
                          <Badge className="bg-[#84bc00]">{itin.status.toUpperCase()}</Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-700">
                          <span>📅 {itin.start_date} → {itin.end_date}</span>
                        </div>
                        {itin.documents?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-bold text-gray-700 mb-2">Documents:</p>
                            {itin.documents.map((doc, idx) => (
                              <Badge key={idx} variant="outline" className="mr-2">{doc}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar size={48} className="text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No confirmed trips yet. Start planning your adventure!</p>
                    <Button className="mt-4 bg-[#84bc00] hover:bg-[#6a9600]" onClick={() => navigate('/packages')}>
                      BROWSE PACKAGES
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Messages</CardTitle>
                <CardDescription>Communicate with our team</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Message List */}
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {messages.length > 0 ? (
                      messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`p-4 rounded-lg ${
                            msg.sender === 'user' ? 'bg-[#84bc00]/10 ml-8' : 'bg-gray-100 mr-8'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="font-bold text-sm">
                              {msg.sender === 'user' ? 'You' : 'Exciting.Travel Team'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-gray-700">{msg.content}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Mail size={48} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">No messages yet. Start a conversation!</p>
                      </div>
                    )}
                  </div>

                  {/* Send Message Form */}
                  <form onSubmit={sendMessage} className="border-t pt-6">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message here..."
                      className="mb-4"
                      rows={4}
                    />
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#84bc00] hover:bg-[#6a9600] font-bold"
                    >
                      {sending ? 'SENDING...' : 'SEND MESSAGE'}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MembersDashboard;
